import { format, isValid } from "date-fns";
import { getStorage } from "../../../../utilities/browserStorage";
import { DEFAULT_DATE_FORMAT, DEFAULT_PAGING_SIZE } from "../../../../utilities/staticConfigs";
import i18n from "../../../../utilities/i18n";

export const generateQueryString = (page, pageSelector, pagination, calledFrom, eraSearchVariables) => {
    try {
        let pageSize;
        if (calledFrom === "search") {
            pageSize = DEFAULT_PAGING_SIZE
        } else if (calledFrom === "PageLimit") {
            pageSize = page;
            page = 1
        } else if (calledFrom === "onPageUp" || calledFrom === "onPageNext" || calledFrom === "onPagePrevious") {
            pageSize = pageSelector.pageLimit;
        }
        let query = `?page_size=${pageSize}&page=${page ?? pagination?.activePage}&practice_pk=${getStorage('practice')}`;
        const eraVariableKeys = [
            'payer',
            'payment_date_from',
            'payment_date_to',
            'era_uploaded_date_from',
            'era_uploaded_date_to',
            'payment_reference',
            'payment_amount',
            'show_type',
            'payment_mode'
        ];

        for (const key of eraVariableKeys) {
            if (eraSearchVariables[key]) {
                let value = eraSearchVariables[key];

                if (key.includes('date')) {
                    const dateValue = typeof value === 'string' ? new Date(value) : value;

                    // Only format if valid date, otherwise leave as-is
                    if (isValid(dateValue)) {
                        value = format(dateValue, 'yyyy-MM-dd');
                    }
                }

                query += `&${key}=${value}`;
            }
        }
        return query;
    } catch (error) {
        console.error(error)
    }
}

/**
 * function to format era adjustment data
 * @param {*} data 
 */
export const eraDataFormatter = (era_adj_result) => {
    let tempPayAdj = [];
    for (let item in era_adj_result) {
        tempPayAdj.push(era_adj_result[item])
    }
    return tempPayAdj;
}

/**
 * function to structure era edit varibales on the top
 * @param {*} data 
 */
export const headerResultFormatter = (data) => {
    let payer_zip_details = '';
    if (data.payer_info && data.payer_info.payer_city) payer_zip_details += `${data.payer_info.payer_city} `;
    if (data.payer_info && data.payer_info.payer_state) payer_zip_details += `${data.payer_info.payer_state} `;
    if (data.payer_info && data.payer_info.payer_zip) payer_zip_details += data.payer_info.payer_zip;

    // Extract payee zip details
    let payee_zip_details = '';
    if (data.payee_info && data.payee_info.payee_city) payee_zip_details += `${data.payee_info.payee_city} `;
    if (data.payee_info && data.payee_info.payee_state) payee_zip_details += `${data.payee_info.payee_state} `;
    if (data.payee_info && data.payee_info.payee_zip) payee_zip_details += data.payee_info.payee_zip.toString().substring(0, 5);

    let paymentVariable = {
        "payer_name": data.payer_info.payer_name,
        "payer_address": data.payer_info.payer_address, "payer_address2": data.payer_info.payer_address2,
        "payer_zip_details": payer_zip_details,
        "payee_name": data.payee_info.payee_name, "payee_address": data.payee_info.payee_address,
        "payee_address2": data.payee_info.payee_address2, "payee_zip_details": payee_zip_details,
        "eft": data.payment_reference_num, "ein": data.ein,
        "check_date": data.payment_date ? format(new Date(data.payment_date), DEFAULT_DATE_FORMAT) : '',
        "check_amount": data.total_check_amount, "production_date": data.era_received_date ? format(new Date(data.era_received_date), DEFAULT_DATE_FORMAT) : '',
        "applied": data.applied ? data.applied : "", "unapplied": data.unapplied ? data.unapplied : "",
    }
    let insuranceVarible = {
        "era_payer": data.payer_info.id,
        "era_payer_name": data.payer_info.payer_name,
        "payment_date": data.payment_date ? format(new Date(data.payment_date), "yyyy-MM-dd") : null,
        "reference_check_number": data.payment_reference_num,
        "amount": data.total_check_amount,
        "payment_mode": 1, "card_type": null,
        "level_adjustments": 0, "adjustment_type": [],
        "era_level_adjustments": data.era_level_adjustments, "era_adjustment_type": data.era_adjustment_type,
        "note": "", "practice": getStorage('practice')

    }

    let paymentAdjustmentAmount = data.era_level_adjustments;

    return { paymentVariable, insuranceVarible, paymentAdjustmentAmount }
}

/**
 * function to structure era summary table data 
 * @param {*} data 
 */
export const summaryResultFromatter = (data) => {
    const groupedData = data.reduce((acc, current) => {
        const {
            adj_amt_1,
            adj_amt_2,
            allowed_amount,
            claim_id, claim_pk,
            closing_balance,
            cpt_charge,
            cpt_payment,
            dos,
            exists_in_sys,
            insurance_payment_pk,
            is_applied, is_manually_added,
            is_reviewed, opening_balance,
            paid_amount,
            patient_id,
            patient_name,
            warning,
            warnings
        } = current;

        if (!acc[claim_pk]) {
            acc[claim_pk] = {
                adj_amt_1,
                adj_amt_2,
                allowed_amount,
                claim_id, claim_pk,
                closing_balance,
                cpt_charge,
                cpt_payment,
                dos,
                exists_in_sys,
                insurance_payment_pk,
                is_applied, is_manually_added,
                is_reviewed, opening_balance,
                paid_amount,
                patient_id,
                patient_name,
                warning,
                warnings
            };
        }

        let dataValues = {
            ...current,
            opening_balance: opening_balance,
            checked: is_reviewed ?? false
        }


        acc[claim_pk] = dataValues;
        return acc;
    }, {});

    return groupedData;

}

/**
 * function to make adjustment code format
 * @param {*} adjustments 
 * @returns 
 */
const getAdjustmentData = (adjustments) => {
    return adjustments?.length ? adjustments.map(item => ({ label: item.adjustment_code, amount: item.amount, id: item.code_id })) : [];
}

/**
 * function to make proceddure data
 * @param {*} result 
 */
export const structureProcedureData = (result, claim_id) => {
    let originalResponsibility = [];
    let jsonObject = {};
    if (result.length > 0) {
        result.forEach((item) => {

            let adjustment_code2_data = getAdjustmentData(item.adjustments?.adjustment2);
            let adjustment_code1_data = getAdjustmentData(item.adjustments?.adjustment1);
            let indexData = {
                'id': item.id,
                'claimId': claim_id, // for frontend use
                'payment': item.payment_pk, 'claim': item.claim_pk, 'claim_custom_id': item.claim_id, 'cpt_code': item.cpt_code, 'patient_id': item.patient_id,
                'allowed_amount': item.allowed_amount ? item.allowed_amount : 0, 'paid_amount': item.paid_amount ? item.paid_amount : 0, 'adjustment_amount1': item.adj_amt_1 ? item.adj_amt_1 : 0, 'adjustment_code1': item.adj_code_1_id ? item.adj_code_1_id : '',
                'adjustment_amount2': item.adj_amt_2 ? item.adj_amt_2 : 0, 'adjustment_code2': item.adj_code_2_id ? item.adj_code_2_id : '', 'opening_balance': item.opening_balance ? Number(item.opening_balance).toFixed(2) : 0,
                'closing_balance': item.closing_balance ? item.closing_balance : 0,
                'charges': item.cpt_charge, 'responsibility_type': item?.next_responsibility?.id, 'next_responsibility': item?.next_responsibility?.id, 'pay_procedure_status': 0, 'adjustment_code2_data': adjustment_code2_data,
                'adjustment_code1_data': adjustment_code1_data, 'procedure': item.procedure_pk, 'exists_in_sys': item.exists_in_sys, 'cpt_not_found': item.cpt_not_found,
                'save_procedure_id': item.save_procedure_id, 'date_of_service': item.date_of_service, 'receipts': Number(item.receipts).toFixed(2),
                'selectedRemarkCodes': item.selected_remark_codes.map(currentItem => { return { ...currentItem, name: currentItem.label } }),
                'remark_codes': item.selected_remark_codes.map(currentItem => { return currentItem.id }),
                'copay': item.copay, 'co_insurance': item.co_insurance, 'deductibles': item.deductibles,
                'is_applied': item?.is_applied,
                'claim_status_id': item.claim_status_id, "claim_sub_status_id": item.claim_sub_status_id,
                "cpt_mod_code": item.cpt_mod_code, "cpt_mod_code2": item.cpt_mod_code2, "cpt_mod_code3": item.cpt_mod_code3, "cpt_mod_code4": item.cpt_mod_code4,
                "current_responsibility": item.current_responsibility,
                "is_manually_added": Boolean(item.is_manually_added),
                "is_reviewed": item.is_reviewed ? item.is_reviewed : false,
                "checked": item.is_reviewed ? item.is_reviewed : false,
                "moved_to_credit": item.moved_to_credit ? item.moved_to_credit : false,
                "paid_by_entity": item.paid_by_entity ? item.paid_by_entity : "",
                "enable_moved_to_credit":enableMovedToCredit(item.paid_by_entity,item.is_primary_paid ,item.is_secondary_paid,item.is_tertiary_paid ),
                "is_first_payment": item.is_first_payment ? item.is_first_payment : false,
                "is_primary_paid": item.is_primary_paid ? item.is_primary_paid : false,
                "is_secondary_paid": item.is_secondary_paid ? item.is_secondary_paid : false,
                "is_tertiary_paid": item.is_tertiary_paid ? item.is_tertiary_paid : false,
            }
            if (item.is_manually_added) {
                indexData.line_item_control_number = item.line_item_control_number || "";
            }

            jsonObject[`New-${crypto?.randomUUID()}`] = indexData;

            originalResponsibility.push({
                patient_pk: item.patient_id,
                responsibility: item?.current_responsibility?.id
            })
        })
    }


    return { jsonObject, originalResponsibility };
}

/**
 * function to check value change or error
 * @param {*} name 
 * @param {*} id 
 * @param {*} index 
 * @param {*} procedureData 
 */
export const checkFoValueChange = (id, procedureData, responsibilityList, claimStatusList) => {
    let claim_status_id = null; let claim_sub_status_id = null; let responbility_id = null;
    let denial = false;
    let res_status=false;
    let currentProcessData = procedureData[id];
    const currentAllowed_amt = parseNumber(currentProcessData.allowed_amount);
    const current_paid_amt = parseNumber(currentProcessData.paid_amount);
    const current_opening_balance = parseNumber(currentProcessData.opening_balance);
    const adjustment_amt1_sum = calculateAdjustmentSum(currentProcessData?.adjustment_code1_data);
    // const adjustment_amt2_sum = calculateAdjustmentSum(currentProcessData?.adjustment_code2_data);
    const closingBalance = parseNumber((current_opening_balance) - (current_paid_amt) - (adjustment_amt1_sum));
    const isClosingBalanceZero = closingBalance <= 0;

    if (isClosingBalanceZero && current_paid_amt != 0) {
        let { claim_status, claim_sub_status, responsibility } = handleChangeResponsibility(currentProcessData, responsibilityList, claimStatusList)
        claim_status_id = claim_status;
        claim_sub_status_id = claim_sub_status;
        responbility_id = responsibility;
    }
    else {
        let { claim_status, claim_sub_status, responsibility, denial_status,res_change_status } = handleAdjustmentCodeCheck(currentProcessData, responsibilityList, current_paid_amt, currentAllowed_amt, claimStatusList)
        claim_status_id = claim_status;
        claim_sub_status_id = claim_sub_status;
        responbility_id = responsibility;
        denial = denial_status
        res_status=res_change_status
    }

    return { claim_status_id, claim_sub_status_id, responbility_id, closingBalance, denial,res_status }
}

/**
 * check for responsibility change 
 * @param {*} currentProcessData 
 * @param {*} responsibilityList 
 */
const handleChangeResponsibility = (currentProcessData, responsibilityList, claimStatusList) => {
    let claim_status = null; let claim_sub_status = null; let responsibility = null;
    const responsibilityData = responsibilityList?.filter(item => item.name === 'Paid');
    let paidItem = "Claim Paid".toLowerCase();
    if (responsibilityData?.length > 0) {
        let claim_status_id = claimStatusList.find(item => item.name.toLowerCase() === paidItem && item?.priorities?.includes(responsibilityData[0].id));
        claim_status = claim_status_id?.id ?? "";
        if (claim_status_id?.sub_status.length > 0) {
            claim_sub_status = claim_status_id?.sub_status.find(item => item.name.toLowerCase() === "paid").id;
        } else {
            claim_sub_status = null
        }
        responsibility = responsibilityData[0].id ?? currentProcessData.next_responsibility;
    } else {
        claim_status = null;
        claim_sub_status = null;
        responsibility = currentProcessData?.current_responsibility?.id ?? currentProcessData.next_responsibility;
    }


    return { claim_status, claim_sub_status, responsibility }
}

/**
 * Here we check for adjustment 2 code 
 * @param {*} currentProcessData 
 * @param {*} responsibilityList 
 */
const handleAdjustmentCodeCheck = (currentProcessData, responsibilityList, current_paid_amt, currentAllowed_amt, claimStatusList) => {
    let claim_status = null; let claim_sub_status = null; let responsibility = null;
    let denial_status = false;
    let res_change_status=false;
    let matchingElements = currentProcessData?.adjustment_code2_data?.findIndex(element => {
        return element.label === "PR-1" || element.label === "PR-2" || element.label === "PR-3";
    });

    if (matchingElements !== -1) {
        if (matchingElements === 0) {
            res_change_status=true;
            switch (currentProcessData?.current_responsibility) {
                case 1: {
                    let responsibilityData = responsibilityList?.filter(item => item.name === 'Secondary');
                    if (responsibilityData?.length==0) {
                        responsibilityData = responsibilityList?.filter(item => item.name === 'Patient');
                    }

                    claim_status = null;
                    claim_sub_status = null;
                    responsibility = responsibilityData?.[0]?.id ?? currentProcessData?.next_responsibility;
                    break;
                }

                case 2: {
                    let responsibilityData = responsibilityList?.filter(item => item.name === 'Tertiary');
                    if (responsibilityData?.length==0) {
                        responsibilityData = responsibilityList?.filter(item => item.name === 'Patient');
                    }

                    claim_status = null;
                    claim_sub_status = null;
                    responsibility = responsibilityData?.[0]?.id ?? currentProcessData?.next_responsibility;
                    break;
                }

                case 3: {
                    let responsibilityData = responsibilityList?.filter(item => item.name === 'Patient');

                    claim_status = null;
                    claim_sub_status = null;
                    responsibility = responsibilityData?.[0]?.id ?? currentProcessData?.next_responsibility;
                    break;
                }

                case 5: {
                    claim_status = null;
                    claim_sub_status = null;
                    responsibility = currentProcessData?.next_responsibility;
                    break;
                }
            }

        } else {
            let responsibilityData = responsibilityList?.filter(item => item.name === 'Primary');
            responsibility = responsibilityData[0]?.id == undefined ? currentProcessData?.next_responsibility : responsibilityData[0]?.id;
        }
    } else if (currentProcessData?.adjustment_code2_data?.length === 0) {
        if (currentAllowed_amt === current_paid_amt && currentProcessData?.adjustment_code2_data.length == 0) {
            const responsibilityData = responsibilityList?.filter(item => item.name === 'Primary');
            if (responsibilityData.length > 0) {
                responsibility = responsibilityData[0].id ?? currentProcessData?.next_responsibility;
                claim_status = null;
                claim_sub_status = null;
            }
            return { claim_status, claim_sub_status, responsibility, denial_status };
        } else if (currentAllowed_amt === current_paid_amt) {
            const responsibilityData = responsibilityList?.filter(item => item.name === 'Paid');
            if (responsibilityData.length > 0) {
                responsibility = responsibilityData[0].id ?? currentProcessData?.next_responsibility
                claim_status = null;
                claim_sub_status = null;
            } else {
                responsibility = currentProcessData?.current_responsibility?.id ?? currentProcessData?.next_responsibility;
            }
        } else {
            claim_status = null;
            claim_sub_status = null;
            responsibility = currentProcessData?.current_responsibility ?? currentProcessData?.next_responsibility;
        }
    } else {
        if (currentProcessData?.adjustment_code2_data.length != 0) {
            responsibility = currentProcessData?.current_responsibility ?? currentProcessData?.next_responsibility;
            const { status_id, sub_status_id } = checkForDenialStatus(currentProcessData?.current_responsibility, claimStatusList, currentProcessData)
            claim_status = status_id ? status_id : null;
            claim_sub_status = sub_status_id ? sub_status_id : null;
            denial_status = true;
        }
        else {
            let responsibilityData = responsibilityList?.filter(item => item.name === 'Primary');
            claim_status = null;
            claim_sub_status = null;
            responsibility = responsibilityData[0].id == undefined ? currentProcessData?.next_responsibility : responsibilityData[0].id;
        }
    }
    return { claim_status, claim_sub_status, responsibility, denial_status,res_change_status };
}

/**
 * check for denial code making responsibility to primary
 * @param {*} responsibility 
 * @param {*} claimStatusList 
 * @param {*} procedure 
 * @returns 
 */
const checkForDenialStatus = (responsibility, claimStatusList, procedure) => {
    let denialItem = "";
    let status_id = "";
    let sub_status_id = "";
    if (responsibility == 1) {
        denialItem = "Claim Denied at Primary".toLowerCase();
    } else if (responsibility == 2) {
        denialItem = "Claim Denied at Secondary".toLowerCase();
    } else {
        denialItem = "Claim Denied at Tertiary".toLowerCase();
    }
    let claim_status_id = claimStatusList.find(item => item.name.toLowerCase() === denialItem && item?.priorities?.includes(responsibility))?.id;
    if (claim_status_id) {
        status_id = claim_status_id;
    }

    const allowedLabels = ["PR-1", "PR-2", "PR-3"];
    let filteredItems = procedure.adjustment_code2_data.filter(item => !allowedLabels.includes(item.label));

    if (filteredItems.length === 0) return null;

    const lastItem = filteredItems[filteredItems.length - 1];

    let claim_sub_status_id = lastItem && lastItem.claim_sub_status && lastItem.claim_sub_status.length > 0
        ? lastItem.claim_sub_status[0].id
        : "";
    if (claim_sub_status_id) {
        sub_status_id = claim_sub_status_id;
    }else{
        sub_status_id=procedure.claim_sub_status_id ?? ""
    }
    return { status_id, sub_status_id }

}




/**
 * function for checking errors in proceddure line
 * @param {*} name 
 * @param {*} id 
 * @param {*} index 
 * @param {*} procedureData 
 */
export const checkForError = (field, id, adjustmentId, procedureData, procedureError) => {
    let currentProcessData = procedureData[id];
    let errors = { ...procedureError[id] };
    const currentAllowed_amt = parseNumber(currentProcessData.allowed_amount);
    const current_paid_amt = parseNumber(currentProcessData.paid_amount);
    const move_to_credit = currentProcessData.moved_to_credit;
    const adjustment_amt1_sum = calculateAdjustmentSum(currentProcessData?.adjustment_code1_data);
    const adjustment_amt2_sum = calculateAdjustmentSum(currentProcessData?.adjustment_code2_data);
    const current_opening_balance = parseNumber(currentProcessData.opening_balance);
    const chargeAmount = parseFloat((current_opening_balance - currentAllowed_amt).toFixed(2));
    const closingBalance = move_to_credit ? current_opening_balance : parseNumber(Number((current_opening_balance) - (current_paid_amt) - (adjustment_amt1_sum)));
    if (currentAllowed_amt > current_opening_balance) {
        errors["allowed_amount"] = 'Allowed amount cannot be greater than charges amount.';
    } else {
        if (errors && Object.prototype.hasOwnProperty.call(errors, 'allowed_amount')) {
            delete errors.allowed_amount;
        }
    }

    if (current_paid_amt > currentAllowed_amt) {
        errors["paid_amount"] = 'Paid amount cannot be greater than Allowed amount.';
    } else {
        if (errors && Object.prototype.hasOwnProperty.call(errors, 'paid_amount')) {
            delete errors.paid_amount;
        }
    }

    if (adjustment_amt1_sum !== 0 && adjustment_amt1_sum !== chargeAmount) {
        let runningSum = 0;
        let isExceeded = false;

        errors["adjustment_amount1"] = {
            ...currentProcessData?.adjustment_code1_data.reduce((errors, adjustmentItem, idx) => {
                    const amt = parseFloat(adjustmentItem.amount || 0);
                    runningSum += amt;

                    const needsError = (
                        adjustment_amt1_sum < chargeAmount ||  // Case 1: total is too small
                        runningSum > chargeAmount ||           // Case 2: overlimit begins
                        isExceeded                             // Case 3: continuing after overlimit
                    );

                    if (needsError) {
                        isExceeded = true;
                        errors[adjustmentItem.id] = {
                            id: adjustmentItem.id,
                            cpt_code: currentProcessData.cpt_code,
                            message: 'Total Adjustment 1 amount should equal to Charge amount - Allowed amount.',
                            label: adjustmentItem?.label,
                            index: idx
                        };
                    }
                return errors;
            }, {})
        }
    }

    else {
        if (errors["adjustment_amount1"]) {
            errors["adjustment_amount1"] = {
                ...currentProcessData?.adjustment_code1_data.reduce((errors, adjustmentItem) => {
                    if (adjustmentItem.label?.toLowerCase() !== "co-253") {
                        delete errors[adjustmentItem.id]
                    }
                    return errors;

                }, {})
            }
        }
    }




    if (adjustment_amt2_sum !== 0 && adjustment_amt2_sum != closingBalance) {
        errors["adjustment_amount2"] = {
            ...currentProcessData?.adjustment_code2_data.reduce((errors, adjustmentItem) => {
                if (adjustmentItem.label?.toLowerCase() !== "co-253") {
                    errors[adjustmentItem.id] = {
                        id: adjustmentItem.id,
                        cpt_code: currentProcessData.cpt_code,
                        message: 'Total Adjustment 2 amount should equal to Charge amount - Paid Amount - Total Adjustment 1 amount.',
                        label: adjustmentItem?.label
                    };
                }
                return errors;

            }, {})
        }
    } else {
        if (errors["adjustment_amount2"]) {
            errors["adjustment_amount2"] = {
                ...currentProcessData?.adjustment_code1_data.reduce((errors, adjustmentItem) => {
                    if (adjustmentItem.label?.toLowerCase() !== "co-253") {
                        delete errors[adjustmentItem.id]
                    }
                    return errors;

                }, {})
            }
        }
    }

    return errors;
}


// Helper functions to reduce complexity
function parseNumber(value) {
    return !isNaN(value) && value !== null && value !== "" ? Number(parseFloat(value).toFixed(2)) : 0;
}

/**
 * function to calculate adjustment sum
 * @param {*} adjustmentData 
 * @returns 
 */
function calculateAdjustmentSum(adjustmentData) {
    return adjustmentData?.reduce((sum, adjustmentItem) => {
        return sum + Number(adjustmentItem?.amount ?? 0);
    }, 0) || 0;
}

export const getSelectedClaimIDs = (data) => {
    let ids = [];
    if (Object.keys(data).length > 0) {
        Object.values(data).forEach(ele => {
            if (ele.checked === true && !ele.is_reviewed) {
                ids.push(ele.claim_pk);
            }
        });
    }
    return ids;
};


export const getReviewedClaims = (data) => {
    let ids = [];
    if (Object.keys(data).length > 0) {
        Object.values(data).forEach(ele => {
            if (ele.checked === true && ele.is_reviewed) {
                ids.push(ele.claim_pk);
            }
        });
    }
    return ids;
};



export function getChangedProcedure(original, updated,newClaim) {
    const numericFields = [
        'allowed_amount', 'paid_amount', 'charges', 'copay',
        'co_insurance', 'deductibles', 'receipts', 'opening_balance', 'closing_balance'
    ];

    const normalizeValue = (key, val) => {
        if (numericFields.includes(key) && typeof val === 'string' && !isNaN(val)) {
            return parseFloat(val);
        }
        return val;
    };

    const isObject = val => val && typeof val === 'object' && !Array.isArray(val);
    const isArray = Array.isArray;

    const deepEqual = (a, b) => {
        const isNumeric = (val) => !isNaN(val) && !isNaN(parseFloat(val));

        if (isNumeric(a) && isNumeric(b)) {
            return parseFloat(a) === parseFloat(b);
        }

        if (a === b) return true;

        if (typeof a !== "object" || typeof b !== "object" || a == null || b == null) {
            return false;
        }

        const keysA = Object.keys(a);
        const keysB = Object.keys(b);
        if (keysA.length !== keysB.length) return false;

        for (let key of keysA) {
            if (!keysB.includes(key) || !deepEqual(a[key], b[key])) {
                return false;
            }
        }

        return true;
    };

    const arraysEqual = (arr1 = [], arr2 = []) => {
        if (arr1.length !== arr2.length) return false;
        return arr1.every((item, index) => deepEqual(item, arr2[index]));
    };

    const isDifferent = (a, b, key = '') => {
        const valA = normalizeValue(key, a);
        const valB = normalizeValue(key, b);

        if(newClaim && key=="paid_by_entity") {
            return false;
        }

        if (isArray(valA) && isArray(valB)) {
            return !arraysEqual(valA, valB);
        } else if (isObject(valA) && isObject(valB)) {
            const keys = new Set([...Object.keys(valA), ...Object.keys(valB)]);
            for (let k of keys) {
                if (isDifferent(valA[k], valB[k], k)) return true;
            }
            return false;
        } else {
            return valA !== valB;
        }
    };

    const jsonObject = {};

    for (const key in updated) {
        const updatedEntry = updated[key];
        const originalEntry = original[key];
        
        if (isDifferent(originalEntry, updatedEntry)) {
            jsonObject[`New-${crypto?.randomUUID?.() || Math.random().toString(36).slice(2)}`] = updatedEntry;
        }
    }

    return jsonObject;
}


export const checkForMandatoryFields = (procedureData) => {
    for (let key in procedureData) {
        let item = procedureData[key];

        if (!item.responsibility_type || !item.claim_status_id || !item.claim_sub_status_id ) {
            return {status:true,message:i18n.t("payments.eraPage.mandatory_field_error")}
        }

        if(!item.paid_by_entity){
            return {status:true,message:i18n.t("payments.post_payments.insurancePayment.paymenMessages.emptyPaidByEntity")}
        }
    }
    return {status:false,message:""};
};


export const checkForWaringErrors = (procedureErrors) => {
    let hasErrors = false;

    function recurse(obj) {
        if (!obj || typeof obj !== 'object') return;

        // Check for string-based direct errors like allowed_amount
        Object.values(obj).forEach((val) => {
            if (typeof val === 'string' && val.trim() !== '') {
                hasErrors = true;
            } else if (typeof val === 'object') {
                recurse(val);
            }
        });
    }

    Object.values(procedureErrors).forEach((entry) => recurse(entry));

    return hasErrors;
};


export const structureSaveProcedureData = (data) => {
    let saveData = [];
    Object.keys(data).forEach(ele1 => {
        let item = data[ele1];
        let adjustment1 = [];
        let adjustment2 = [];
        if (item.adjustment_code1_data && item.adjustment_code1_data.length) {
            item.adjustment_code1_data.forEach(adjitem1 => {
                // Check if the item already exists in adjustment1
                if (!adjustment1.some(adj => adj.id === adjitem1.id)) {
                    adjustment1.push({
                        code: adjitem1.label,
                        amount: adjitem1.amount,
                        id: adjitem1.id
                    });
                }
            });
        }

        if (item.adjustment_code2_data && item.adjustment_code2_data.length) {
            item.adjustment_code2_data.forEach(adjitem2 => {
                // Check if the item already exists in adjustment2
                if (!adjustment2.some(adj => adj.id === adjitem2.id)) {
                    adjustment2.push({
                        code: adjitem2.label,
                        amount: adjitem2.amount,
                        id: adjitem2.id
                    });
                }
            });
        }

        let values = {
            "id": item.id ? item.id : null,
            "claim_id": item.claim_custom_id ? item.claim_custom_id : null,
            "date_of_service": item.date_of_service ? item.date_of_service : null,
            "cpt_payment": item.paid_amount ? item.paid_amount : 0,
            "is_manually_added": item.is_manually_added ? item.is_manually_added : false,
            "claim_pk": item.claim ? item.claim : null,
            "cpt_code": item.cpt_code ? item.cpt_code : null,
            "cpt_mod_code": item.cpt_mod_code ? item.cpt_mod_code : null,
            "cpt_mod_code2": item.cpt_mod_code2 ? item.cpt_mod_code2 : null,
            "cpt_mod_code3": item.cpt_mod_code3 ? item.cpt_mod_code3 : null,
            "cpt_mod_code4": item.cpt_mod_code4 ? item.cpt_mod_code4 : null,
            "remark_codes": item.selectedRemarkCodes.length > 0 
                ? item.selectedRemarkCodes.map(code => code.id)
                : [],
            "allowed_amount": item.allowed_amount ? item.allowed_amount : 0,
            "copay": item.copay ? item.copay : 0,
            "coInsurance": item.coInsurance ? item.coInsurance : 0,
            "deductibles": item.deductibles ? item.deductibles : 0,
            "adjustments": {
                "adjustment1": adjustment1 ? adjustment1 : [],
                "adjustment2": adjustment2 ? adjustment2 : []
            },
            "responsibility_type": item.responsibility_type ? item.responsibility_type : "",
            "exists_in_sys": item.exists_in_sys ? item.exists_in_sys : false,
            "is_reviewed": true,
            "cpt_not_found": item.cpt_not_found ? item.cpt_not_found : false,
            "claim_status_id": item.claim_status_id ? item.claim_status_id : null,
            "claim_sub_status_id": item.claim_sub_status_id ? item.claim_sub_status_id : null,
            "moved_to_credit": item.moved_to_credit ? item.moved_to_credit : false,
            "paid_by_entity": item.paid_by_entity ? item.paid_by_entity : "",
        }

        if(values?.enable_moved_to_credit){
            delete values["enable_moved_to_credit"]
        }

        if (item.is_manually_added) {
            values.line_item_control_number = item.line_item_control_number || ""
        }
        saveData.push(values);
    })

    return saveData;

}



/**
 * this function check the condition to enable or disable moved to credit 
 * @param {*} paid_by_entity 
 * @param {*} paid_status 
 */
export const enableMovedToCredit = (
    paid_by_entity,
    is_primary_paid = false,
    is_secondary_paid = false,
    is_tertiary_paid = false
) => {
    const paidStatus = {
        1: is_primary_paid,
        2: is_secondary_paid,
        3: is_tertiary_paid,
    };

    return Boolean(paidStatus[paid_by_entity]);
};


